import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from '../../../useTranslation'
import { toast } from 'react-toastify'
import { AddressDTO, AddressDTOTypeEnum, AddressDTOVerificationStatusEnum, AddressOriginType, MasterDataDTO } from '@ringo/shared'
import { useUser } from '../../../common/hooks/auth/useUser'
import Button from '../../../components/common/button'
import IconBox from '../../../components/common/icon-box'
import UpdateAddressModal from '../../../components/common/modal/modal-address'
import AddressPreview from '../../../components/common/address-preview'
import { useAddressApi } from '../../../common/api-context'
import useConfirm from '../../../common/hooks/useConfirm'

interface AddressBoxProps {
    address: AddressDTO
    pendingMasterAddressChange?: boolean
    pendingLivingAddressChange?: boolean
    shoppingCartAddressChoosing?: boolean
    onChooseShoppingCartAddress?: (id: string) => void
    disableDelete?: boolean
    canSelectForShipping?: boolean
}

const AddressBox: React.FC<AddressBoxProps> = ({
    address,
    pendingMasterAddressChange = false,
    pendingLivingAddressChange = false,
    shoppingCartAddressChoosing,
    onChooseShoppingCartAddress,
    disableDelete = false,
    canSelectForShipping,
}) => {
    const [showAddressModal, setShowAddressModal] = useState<boolean>(false)
    const [masterAddress, setMasterAddress] = useState<MasterDataDTO>()
    const [getActionConfirmation, ActionConfirmation] = useConfirm()

    const { t } = useTranslation()
    const addressApi = useAddressApi()
    const { refreshUserData } = useUser()

    const isDeliveryAddress = address.type === AddressDTOTypeEnum.Delivery

    const setDefaultAddress = useMutation({
        mutationFn: (addressId: string) => {
            return addressApi.setDefault(addressId)
        },
        onSuccess: () => {
            refreshUserData()
            toast.success(t('myAccount.addresses.successDefaultAddress'))
        },
        onError: () => {
            toast.error(t('myAccount.addresses.errorDefaultAddress'))
        },
    })

    const deleteAddress = useMutation({
        mutationFn: (addressId: string) => {
            return addressApi.deleteAddress(addressId)
        },
        onSuccess: () => {
            refreshUserData()
            toast.success(t('myAccount.addresses.addressDeleted'))
        },
        onError: () => {
            toast.error(t('myAccount.addresses.errorDeletingAddress'))
        },
    })

    const handleAddressDeletion = async (addressId: string) => {
        if (
            !(await getActionConfirmation({
                description: t('myAccount.addresses.confirmAddressDeleteDesc'),
                title: t('myAccount.addresses.confirmAddressDelete'),
                textRightType: 'squareBlue',
                textLeftType: 'squareYellow',
                textLeftText: t('cancel'),
                textRightText: t('delete'),
                iconLeft: 'xmark',
                iconRight: 'trash',
            }))
        ) {
            return
        }
        deleteAddress.mutate(addressId)
    }

    return (
        <div>
            <ActionConfirmation />
            {masterAddress && <UpdateAddressModal open={showAddressModal} onClose={() => setShowAddressModal(false)} address={masterAddress} />}
            <IconBox icon={`${isDeliveryAddress ? 'truck' : 'wallet'}`}>
                <div className="pt-11 pb-6 px-2">
                    <div className="flex justify-start">
                        <p className="font-bold text-xl text-left capitalize leading-none max-tablet:text-base">
                            {t(isDeliveryAddress ? 'yourBasket.deliveryAddress' : 'yourBasket.baseAddress')}
                        </p>
                        {isDeliveryAddress && !address.defaultAddress && !disableDelete && address.addressOriginType === AddressOriginType.RingoDb && (
                            <FontAwesomeIcon
                                icon={['fas', 'trash']}
                                className="text-md px-2 py-[3px] cursor-pointer max-tablet:text-xs"
                                onClick={() => handleAddressDeletion(address.id as string)}
                            />
                        )}
                    </div>
                    <hr className="my-3 max-tablet:my-2" />
                    <p className="text-left my-4 text-black text-base max-tablet:text-sm max-tablet:my-3">
                        <AddressPreview address={address} />
                    </p>
                    <div className="w-full h-20 flex justify-end flex-col">
                        {(pendingMasterAddressChange || pendingLivingAddressChange) && (
                            <div className="w-full flex justify-center">
                                <FontAwesomeIcon icon={['fas', 'circle-info']} className="text-custom_blue text-sm pr-2 max-tablet:mt-[2px]" />
                                <p className="text-base text-black leading-none max-tablet:text-sm">
                                    {t(pendingMasterAddressChange ? 'myAccount.addresses.pendingAddressChange' : 'myAccount.addresses.addressNotYetVerified')}
                                </p>
                            </div>
                        )}
                        {isDeliveryAddress ? (
                            !shoppingCartAddressChoosing ? (
                                !address.defaultAddress &&
                                address.addressOriginType === AddressOriginType.RingoDb &&
                                address.verificationStatus === AddressDTOVerificationStatusEnum.Verified && (
                                    <div className="w-full mt-2.5 max-tablet:mt-2">
                                        {!shoppingCartAddressChoosing && (
                                            <Button
                                                type="squareYellow"
                                                icon="check"
                                                text={t('myAccount.addresses.setAsDefault')}
                                                onClick={() => setDefaultAddress.mutate(address.id as string)}
                                                isLoading={deleteAddress.isPending || setDefaultAddress.isPending}
                                            />
                                        )}
                                    </div>
                                )
                            ) : (
                                <Button
                                    type="squareBlue"
                                    icon="check"
                                    text={t('useAddress')}
                                    onClick={() => onChooseShoppingCartAddress?.(address.id as string)}
                                    className="mt-2"
                                    disabled={!canSelectForShipping}
                                />
                            )
                        ) : (
                            <div className="w-full mt-2.5 max-tablet:mt-2">
                                <Button
                                    type="squareBlue"
                                    icon="gear"
                                    text={t('myAccount.addresses.requestChange')}
                                    onClick={() => !pendingMasterAddressChange && (setShowAddressModal(true), setMasterAddress(address))}
                                    disabled={pendingMasterAddressChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </IconBox>
        </div>
    )
}

export default AddressBox
